import { useBreakpointValue } from '@chakra-ui/react';
import React, { useMemo } from 'react';

import { CardVariant } from '@/components/chakra-ui';
import { SectionSize, SectionType } from '@/layout';
import { t } from '@/utils';
import { WorksSectionLayout } from '@/views/business/viral/sections/works';

const WorksSection = (): JSX.Element => {
  const variant = useBreakpointValue<CardVariant>({ base: `rounded`, md: `flat` });
  const contentNode = useMemo(
    () => (
      <>
        <WorksSectionLayout
          title="블로그 체험단"
          titleProps={{
            pb: { base: `1.5rem`, md: `0.2rem` },
            textAlign: { base: `center`, md: `left` },
          }}
          sectionProps={{
            name: `worksBlog`,
            sectionSize: SectionSize.MD,
            pbMult: 0.75,
          }}
          cardProps={{
            variant,
            contentWrapProps: {
              pos: `relative`,
              left: { md: `-1.2rem` },
              borderTopRadius: { base: `2xl`, md: undefined },
            },
            contents: [
              {
                key: t(`pages.business.viral.sections.works.sections.blog.title1`),
                title: t(`pages.business.viral.sections.works.sections.blog.title1`),
                body: [
                  t(`pages.business.viral.sections.works.sections.blog.body1-1`),
                  t(`pages.business.viral.sections.works.sections.blog.body1-2`),
                ],
              },
              {
                key: t(`pages.business.viral.sections.works.sections.blog.title2`),
                title: t(`pages.business.viral.sections.works.sections.blog.title2`),
                body: [
                  t(`pages.business.viral.sections.works.sections.blog.body2-1`),
                  t(`pages.business.viral.sections.works.sections.blog.body2-2`),
                  t(`pages.business.viral.sections.works.sections.blog.body2-3`),
                  t(`pages.business.viral.sections.works.sections.blog.body2-4`),
                ],
              },
              {
                key: t(`pages.business.viral.sections.works.sections.blog.title3`),
                title: t(`pages.business.viral.sections.works.sections.blog.title3`),
                body: [
                  t(`pages.business.viral.sections.works.sections.blog.body3-1`),
                  t(`pages.business.viral.sections.works.sections.blog.body3-2`),
                ],
              },
            ],
            imgSrc: `/pages/business/viral/sections/works/bg_blog.png`,
            imgProps: {
              maxW: `40rem`,
              pos: { base: `relative`, md: `absolute` },
              left: { 'md': `40vw`, '2xl': `50vw` },
              bottom: { md: `-10%` },
              zIndex: 2,
            },
            reverse: true,
          }}
        />
        <WorksSectionLayout
          title="인스타그램 체험단"
          titleProps={{
            p: { base: `0 0 2rem 0`, md: `0 14rem` },
            textAlign: { base: `center`, md: `right` },
          }}
          sectionProps={{
            name: `worksSns`,
            sectionType: SectionType.GRAY,
            sectionSize: SectionSize.LG,
            pbMult: 0.75,
          }}
          cardProps={{
            variant,
            cardWrapProps: {
              bg: { base: `bg.gray`, md: `bg.gray` },
              maxHeight: { md: `30%` },
            },
            contentWrapProps: {
              bg: { base: `white`, md: `bg.gray` },
              gap: `2rem`,
              borderTopRadius: { base: `2xl`, md: undefined },
            },
            contents: [
              {
                key: t(`pages.business.viral.sections.works.sections.sns.title1`),
                title: t(`pages.business.viral.sections.works.sections.sns.title1`),
                body: [
                  t(`pages.business.viral.sections.works.sections.sns.body1-1`),
                  t(`pages.business.viral.sections.works.sections.sns.body1-2`),
                ],
              },
              {
                key: t(`pages.business.viral.sections.works.sections.sns.title2`),
                title: t(`pages.business.viral.sections.works.sections.sns.title2`),
                body: [
                  t(`pages.business.viral.sections.works.sections.sns.body2-1`),
                  t(`pages.business.viral.sections.works.sections.sns.body2-2`),
                  t(`pages.business.viral.sections.works.sections.sns.body2-3`),
                  t(`pages.business.viral.sections.works.sections.sns.body2-4`),
                  t(`pages.business.viral.sections.works.sections.sns.body2-5`),
                ],
              },
              {
                key: t(`pages.business.viral.sections.works.sections.sns.title3`),
                title: t(`pages.business.viral.sections.works.sections.sns.title3`),
                body: [
                  t(`pages.business.viral.sections.works.sections.sns.body3-1`),
                  t(`pages.business.viral.sections.works.sections.sns.body3-2`),
                ],
              },
            ],
            imgSrc: `/pages/business/viral/sections/works/bg_insta.png`,
            imgProps: {
              right: { 'md': `60vw`, 'xl': `59vw`, '2xl': `57vw`, '3xl': `55vw` },
              maxW: `30rem`,
              pos: { base: `relative`, md: `absolute` },
              top: { md: `23%` },
              zIndex: 2,
            },
            reverse: false,
          }}
        />
        <WorksSectionLayout
          title="카페&커뮤니티 입소문 마케팅"
          titleProps={{ textAlign: { md: `left` } }}
          sectionProps={{
            name: `worksViral`,
            sectionSize: SectionSize.LG,
            pbMult: 0.75,
          }}
          cardProps={{
            variant,
            contentWrapProps: {
              gap: `2rem`,
              pos: `relative`,
              left: { md: `-3.8rem` },
              borderTopRadius: { base: `2xl`, md: undefined },
            },
            contents: [
              {
                key: t(`pages.business.viral.sections.works.sections.viral.title1`),
                title: t(`pages.business.viral.sections.works.sections.viral.title1`),
                body: [
                  t(`pages.business.viral.sections.works.sections.viral.body1-1`),
                  t(`pages.business.viral.sections.works.sections.viral.body1-2`),
                ],
              },
              {
                key: t(`pages.business.viral.sections.works.sections.viral.title2`),
                title: t(`pages.business.viral.sections.works.sections.viral.title2`),
                body: [
                  t(`pages.business.viral.sections.works.sections.viral.body2-1`),
                  t(`pages.business.viral.sections.works.sections.viral.body2-2`),
                  t(`pages.business.viral.sections.works.sections.viral.body2-3`),
                  t(`pages.business.viral.sections.works.sections.viral.body2-4`),
                ],
              },
              {
                key: t(`pages.business.viral.sections.works.sections.viral.title3`),
                title: t(`pages.business.viral.sections.works.sections.viral.title3`),
                body: [
                  t(`pages.business.viral.sections.works.sections.viral.body3-1`),
                  t(`pages.business.viral.sections.works.sections.viral.body3-2`),
                ],
              },
              {
                key: t(`pages.business.viral.sections.works.sections.viral.title4`),
                title: t(`pages.business.viral.sections.works.sections.viral.title4`),
                body: [
                  t(`pages.business.viral.sections.works.sections.viral.body4-1`),
                  t(`pages.business.viral.sections.works.sections.viral.body4-2`),
                ],
              },
            ],
            imgSrc: `/pages/business/viral/sections/works/bg_cafe.png`,
            imgProps: {
              maxW: { base: `40rem`, md: `50rem` },
              pos: { base: `relative`, md: `absolute` },
              left: { 'md': `40vw`, '2xl': `50vw` },
              bottom: `10%`,
              zIndex: 2,
            },
            reverse: true,
          }}
        />
      </>
    ),
    [variant],
  );

  return contentNode;
};

export default WorksSection;
